/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — docx-statements.ts
   Statement data builders (Balance Sheet, Income Statement, Equity,
   Cash Flow) for the Word (.docx) export pipeline. Each builder returns
   an Array-of-Arrays representation that aoaToTable() renders.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import {
  sum, rows, computeNetIncome,
} from './utils';
import {
  mergeLabels, findNoncashItems, computeWorkingCapitalChanges,
  detectInvestingActivities, detectFinancingActivities,
  type CashFlowLineItem,
} from './statements';

export function buildBSData(multi: boolean): (string | number)[][] {
  const aoa: (string | number)[][] = [];

  function addSection(sectionLabel: string, sectionKey: string): void {
    aoa.push([sectionLabel]);
    const items = mergeLabels(
      rows(state.currentData, sectionKey),
      multi ? rows(state.priorData, sectionKey) : [],
      multi,
    );
    items.forEach((item) => {
      if (multi) aoa.push(['  ' + item.label, item.cur ?? 0, item.prior ?? 0]);
      else aoa.push(['  ' + item.label, item.cur ?? 0]);
    });
  }

  function addTotal(label: string, curVal: number, prior: number): void {
    if (multi) aoa.push([label, curVal, prior]);
    else aoa.push([label, curVal]);
  }

  const totalCA = sum(state.currentData, 'current-assets');
  const totalNCA = sum(state.currentData, 'noncurrent-assets');
  const totalAssets = totalCA + totalNCA;
  const totalCL = sum(state.currentData, 'current-liab');
  const totalNCL = sum(state.currentData, 'noncurrent-liab');
  const totalLiab = totalCL + totalNCL;
  const bsNI = computeNetIncome(state.currentData);
  const totalEquity = sum(state.currentData, 'equity') + bsNI;
  const totalLE = totalLiab + totalEquity;

  let pCA = 0, pNCA = 0, pAssets = 0, pCL = 0, pNCL = 0, pLiab = 0, pEq = 0, pLE = 0, pNI = 0;
  if (multi) {
    pCA = sum(state.priorData, 'current-assets');
    pNCA = sum(state.priorData, 'noncurrent-assets');
    pAssets = pCA + pNCA;
    pCL = sum(state.priorData, 'current-liab');
    pNCL = sum(state.priorData, 'noncurrent-liab');
    pLiab = pCL + pNCL;
    pNI = computeNetIncome(state.priorData);
    pEq = sum(state.priorData, 'equity') + pNI;
    pLE = pLiab + pEq;
  }

  aoa.push(['ASSETS']);
  addSection('Current Assets', 'current-assets');
  addTotal('Total Current Assets', totalCA, pCA);
  addSection('Non-Current Assets', 'noncurrent-assets');
  addTotal('Total Non-Current Assets', totalNCA, pNCA);
  addTotal('TOTAL ASSETS', totalAssets, pAssets);
  aoa.push(['']);
  aoa.push(['LIABILITIES & EQUITY']);
  addSection('Current Liabilities', 'current-liab');
  addTotal('Total Current Liabilities', totalCL, pCL);
  addSection('Non-Current Liabilities', 'noncurrent-liab');
  addTotal('Total Non-Current Liabilities', totalNCL, pNCL);
  addTotal('Total Liabilities', totalLiab, pLiab);
  addSection("Shareholders' Equity", 'equity');
  if (multi) aoa.push(['  Net Income', bsNI, pNI]);
  else aoa.push(['  Net Income', bsNI]);
  addTotal('Total Equity', totalEquity, pEq);
  addTotal('TOTAL LIABILITIES & EQUITY', totalLE, pLE);

  return aoa;
}

export function buildISData(multi: boolean): (string | number)[][] {
  const aoa: (string | number)[][] = [];

  function addSection(sectionLabel: string, sectionKey: string): void {
    aoa.push([sectionLabel]);
    const items = mergeLabels(
      rows(state.currentData, sectionKey),
      multi ? rows(state.priorData, sectionKey) : [],
      multi,
    );
    items.forEach((item) => {
      if (multi) aoa.push(['  ' + item.label, item.cur ?? 0, item.prior ?? 0]);
      else aoa.push(['  ' + item.label, item.cur ?? 0]);
    });
  }

  const totalRev = sum(state.currentData, 'revenue');
  const totalCOGS = sum(state.currentData, 'cogs');
  const grossProfit = totalRev - totalCOGS;
  const totalOpex = sum(state.currentData, 'opex');
  const opIncome = grossProfit - totalOpex;
  const totalOther = sum(state.currentData, 'other');
  const netIncome = opIncome + totalOther;

  let pRev = 0, pCOGS = 0, pGP = 0, pOpex = 0, pOpInc = 0, pOther = 0, pNI = 0;
  if (multi) {
    pRev = sum(state.priorData, 'revenue');
    pCOGS = sum(state.priorData, 'cogs');
    pGP = pRev - pCOGS;
    pOpex = sum(state.priorData, 'opex');
    pOpInc = pGP - pOpex;
    pOther = sum(state.priorData, 'other');
    pNI = pOpInc + pOther;
  }

  function pushTotal(label: string, curVal: number, prior: number): void {
    if (multi) aoa.push([label, curVal, prior]);
    else aoa.push([label, curVal]);
  }

  addSection('Revenue', 'revenue');
  pushTotal('Total Revenue', totalRev, pRev);
  addSection('Cost of Goods Sold', 'cogs');
  pushTotal('Total Cost of Goods Sold', totalCOGS, pCOGS);
  pushTotal('Gross Profit', grossProfit, pGP);
  addSection('Operating Expenses', 'opex');
  pushTotal('Total Operating Expenses', totalOpex, pOpex);
  pushTotal('Operating Income', opIncome, pOpInc);

  if (rows(state.currentData, 'other').length || (multi && rows(state.priorData, 'other').length)) {
    addSection('Other Income / (Expense)', 'other');
    pushTotal('Total Other Income / (Expense)', totalOther, pOther);
  }

  pushTotal('Net Income', netIncome, pNI);
  return aoa;
}

export function buildEquityData(multi: boolean): (string | number)[][] {
  const aoa: (string | number)[][] = [];
  const netIncome = computeNetIncome(state.currentData);
  const totalEquity = sum(state.currentData, 'equity') + netIncome;
  const pTotalEquity = multi ? sum(state.priorData, 'equity') : 0;
  const pNetIncome = multi ? computeNetIncome(state.priorData) : 0;
  const pTotalEquityWithNI = pTotalEquity + pNetIncome;

  if (multi) {
    aoa.push(['Prior Period']);
    aoa.push(['  Beginning Balance', 0]);
    aoa.push(['  Net Income', pNetIncome]);
    rows(state.priorData, 'equity').forEach((item) => {
      if (item.label.toLowerCase().indexOf('retained') === -1) {
        aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
      }
    });
    aoa.push(['Ending Balance (Prior Period)', pTotalEquityWithNI]);
    aoa.push(['']);
    aoa.push(['Current Period']);
    aoa.push(['  Beginning Balance', pTotalEquityWithNI]);
    aoa.push(['  Net Income', netIncome]);
    rows(state.currentData, 'equity').forEach((item) => {
      if (item.label.toLowerCase().indexOf('retained') === -1) {
        aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
      }
    });
    aoa.push(['Ending Balance (Current Period)', totalEquity]);
  } else {
    aoa.push(['Beginning Balance', pTotalEquityWithNI]);
    aoa.push(['  Net Income', netIncome]);
    rows(state.currentData, 'equity').forEach((item) => {
      if (item.label.toLowerCase().indexOf('retained') === -1) {
        aoa.push(['  ' + item.label, parseFloat(String(item.amount)) || 0]);
      }
    });
    aoa.push(['Ending Balance', totalEquity]);
  }

  return aoa;
}

export function buildCFData(multi: boolean): (string | number)[][] {
  const aoa: (string | number)[][] = [];

  function addRow(label: string, curVal: number, prior: number, indent?: boolean): void {
    const prefix = indent ? '  ' : '';
    if (multi) aoa.push([prefix + label, curVal, prior]);
    else aoa.push([prefix + label, curVal]);
  }

  function pushTotal(label: string, curVal: number, prior: number): void {
    if (multi) aoa.push([label, curVal, prior]);
    else aoa.push([label, curVal]);
  }

  const netIncome = computeNetIncome(state.currentData);
  const pNetIncome = multi ? computeNetIncome(state.priorData) : 0;
  const manualOpLabels = rows(state.currentData, 'cf-operating').map((r) => r.label);
  const pManualOpLabels = multi ? rows(state.priorData, 'cf-operating').map((r) => r.label) : [];
  const noncash = findNoncashItems(state.currentData, manualOpLabels);
  const pNoncash = multi ? findNoncashItems(state.priorData, pManualOpLabels) : [];
  const wcChanges = multi ? computeWorkingCapitalChanges(state.currentData, state.priorData) : [];
  const autoTotal = noncash.reduce((s, i) => s + i.amount, 0);
  const pAutoTotal = pNoncash.reduce((s, i) => s + i.amount, 0);
  const wcTotal = wcChanges.reduce((s, i) => s + i.amount, 0);
  const manualOpTotal = sum(state.currentData, 'cf-operating');
  const pManualOpTotal = multi ? sum(state.priorData, 'cf-operating') : 0;
  const totalOp = netIncome + autoTotal + wcTotal + manualOpTotal;
  const pTotalOp = multi ? (pNetIncome + pAutoTotal + pManualOpTotal) : 0;
  const autoInvItems: CashFlowLineItem[] = multi
    ? detectInvestingActivities(state.currentData, state.priorData) : [];
  const autoFinItems: CashFlowLineItem[] = multi
    ? detectFinancingActivities(state.currentData, state.priorData) : [];
  const autoInvTotal = autoInvItems.reduce((s, i) => s + i.amount, 0);
  const autoFinTotal = autoFinItems.reduce((s, i) => s + i.amount, 0);
  const manualInvTotal = sum(state.currentData, 'cf-investing');
  const manualFinTotal = sum(state.currentData, 'cf-financing');
  const pManualInvTotal = multi ? sum(state.priorData, 'cf-investing') : 0;
  const pManualFinTotal = multi ? sum(state.priorData, 'cf-financing') : 0;
  const totalInv = autoInvTotal + manualInvTotal;
  const totalFin = autoFinTotal + manualFinTotal;
  const pTotalInv = pManualInvTotal;
  const pTotalFin = pManualFinTotal;
  const netChange = totalOp + totalInv + totalFin;
  const pNetChange = multi ? (pTotalOp + pTotalInv + pTotalFin) : 0;

  aoa.push(['Cash Flows from Operating Activities']);
  addRow('Net Income', netIncome, pNetIncome, true);

  if (noncash.length > 0 || (multi && pNoncash.length > 0)) {
    aoa.push(['  Adjustments for non-cash items:']);
    if (multi) {
      const allNc = mergeLabels(
        noncash.map((i) => ({ label: i.label, amount: i.amount })),
        pNoncash.map((i) => ({ label: i.label, amount: i.amount })),
        true,
      );
      allNc.forEach((item) => addRow(item.label, item.cur ?? 0, item.prior ?? 0, true));
    } else {
      noncash.forEach((item) => addRow(item.label, item.amount, 0, true));
    }
  }

  if (wcChanges.length > 0) {
    aoa.push(['  Changes in operating assets and liabilities:']);
    wcChanges.forEach((item) => {
      if (multi) aoa.push(['    ' + item.label, item.amount, 0]);
      else aoa.push(['    ' + item.label, item.amount]);
    });
  }

  const manualOp = mergeLabels(
    rows(state.currentData, 'cf-operating'),
    multi ? rows(state.priorData, 'cf-operating') : [],
    multi,
  );
  if (manualOp.length > 0) {
    aoa.push(['  Other operating adjustments:']);
    manualOp.forEach((item) => {
      if (multi) aoa.push(['    ' + item.label, item.cur ?? 0, item.prior ?? 0]);
      else aoa.push(['    ' + item.label, item.cur ?? 0]);
    });
  }

  pushTotal('Net Cash from Operating Activities', totalOp, pTotalOp);

  aoa.push(['Cash Flows from Investing Activities']);
  autoInvItems.forEach((item) => {
    if (multi) aoa.push(['  ' + item.label, item.amount, item.priorAmount]);
    else aoa.push(['  ' + item.label, item.amount]);
  });
  const manualInvItems = mergeLabels(
    rows(state.currentData, 'cf-investing'),
    multi ? rows(state.priorData, 'cf-investing') : [],
    multi,
  );
  manualInvItems.forEach((item) => {
    if (multi) aoa.push(['  ' + item.label, item.cur ?? 0, item.prior ?? 0]);
    else aoa.push(['  ' + item.label, item.cur ?? 0]);
  });
  pushTotal('Net Cash from Investing Activities', totalInv, pTotalInv);

  aoa.push(['Cash Flows from Financing Activities']);
  autoFinItems.forEach((item) => {
    if (multi) aoa.push(['  ' + item.label, item.amount, item.priorAmount]);
    else aoa.push(['  ' + item.label, item.amount]);
  });
  const manualFinItems = mergeLabels(
    rows(state.currentData, 'cf-financing'),
    multi ? rows(state.priorData, 'cf-financing') : [],
    multi,
  );
  manualFinItems.forEach((item) => {
    if (multi) aoa.push(['  ' + item.label, item.cur ?? 0, item.prior ?? 0]);
    else aoa.push(['  ' + item.label, item.cur ?? 0]);
  });
  pushTotal('Net Cash from Financing Activities', totalFin, pTotalFin);
  pushTotal('Net Change in Cash', netChange, pNetChange);

  return aoa;
}
